import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { fetchTicketEvidence, removeTicketSkill } from '../api/team'
import { fetchCandidateTicketEvidence } from '../api/skills'
import Icon from './Icon'
import VoteButtons from './VoteButtons'
import styles from './CaseEvidence.module.css'

// The tabbed evidence card at the bottom of the modal. Each tab maps to one of
// the payload's parallel arrays; `itemsOf` flattens an entry to a selectable
// pill `name` plus the `detail` text shown when that pill is selected.
const TABS = [
  {
    key: 'skills', label: 'Skills',
    itemsOf: ev => (ev.skills ?? []).map(s => ({ id: s.id, name: s.name, detail: s.reason })),
  },
  {
    key: 'symptoms', label: 'Symptoms',
    itemsOf: ev => (ev.symptoms ?? []).map(s => ({
      name: s.name, detail: parseSymptomDetail(s.symptom_detail),
    })),
  },
  {
    key: 'problems', label: 'Problems',
    itemsOf: ev => (ev.problems ?? []).map(p => ({ name: p.name, detail: p.description })),
  },
]

// symptom_detail arrives as a JSON-encoded string, e.g.
// '{"name": "http 500 error", "description": "…"}'. Pull out the description;
// fall back to the raw string if it isn't parseable JSON.
function parseSymptomDetail(detail) {
  if (!detail) return ''
  try {
    const parsed = JSON.parse(detail)
    return parsed.description ?? detail
  } catch {
    return detail
  }
}

/**
 * CaseEvidence — modal that displays AI-generated evidence for a single ticket.
 *
 * Props:
 *   ticketId     {string|number} — the ticket id to fetch evidence for (and the
 *                                  header fallback until the payload's number loads)
 *   primarySkill {string}        — the skill this evidence is being shown for; on the
 *                                  Skills tab it's moved to the first pill and selected
 *   candidateSkillId {number|string} — when set, this evidence is for a reviewable skill
 *                                  candidate (Proposed/Candidates/Rejected). Candidates have
 *                                  no Neo4j presence, so evidence is fetched from Postgres
 *                                  (fetchCandidateTicketEvidence) instead of the graph-backed
 *                                  fetchTicketEvidence the Skills Coverage list uses.
 *   onClose      {function}      — called when the modal is dismissed
 */
export default function CaseEvidence({ ticketId, primarySkill, candidateSkillId, onClose }) {
  const [ev,          setEv]          = useState(null)
  const [tab,         setTab]         = useState('skills')
  const [selected,    setSelected]    = useState(0)
  const [overflowing, setOverflowing] = useState(false)
  const [loading,     setLoading]     = useState(true)
  const [error,       setError]       = useState(null)
  const [removeError, setRemoveError] = useState(null)
  const measureRef = useRef(null)

  // The modal mounts fresh per ticket (CaseBadge renders it only while open), so
  // the initial state (loading/null) is the reset. `active` guards against a late
  // response after unmount.
  useEffect(() => {
    let active = true
    const fetchEvidence = candidateSkillId != null
      ? fetchCandidateTicketEvidence(candidateSkillId, ticketId)
      : fetchTicketEvidence(ticketId)
    fetchEvidence
      .then(d => { if (active) setEv(d.evidence) })
      .catch(e => { if (active) setError(e.message) })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [ticketId, candidateSkillId])

  // Pick pills vs. dropdown by measuring how many rows the items wrap to. The
  // hidden measurer wraps the pills at the real content width; counting the
  // distinct pill offsetTop values gives the row count. Pills show for 1–2 rows;
  // 3+ rows falls back to the dropdown. Re-runs on tab/data and width changes.
  useLayoutEffect(() => {
    const el = measureRef.current
    if (!el) return
    const measure = () => {
      const rows = new Set(Array.from(el.children, c => c.offsetTop))
      setOverflowing(rows.size > 2)
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [tab, ev, primarySkill])

  function handleBackdropClick(e) {
    if (e.target === e.currentTarget) onClose()
  }

  // A stale removal-failure banner shouldn't linger once the user has moved on
  // to a different tab or item.
  useEffect(() => {
    setRemoveError(null)
  }, [tab, selected])

  // Switching tabs always lands on the first pill of the new tab.
  function selectTab(key) {
    setTab(key)
    setSelected(0)
  }

  // Removes the skill from the ticket after a confirmed downvote. Only updates
  // local state on success — a failed delete shouldn't look like it worked.
  async function handleSkillRemoved(skillId) {
    try {
      await removeTicketSkill(ev.ticket_number, skillId)
    } catch (e) {
      setRemoveError(e.message || 'Failed to remove the skill from this case.')
      return
    }
    setRemoveError(null)
    setEv(prev => prev && { ...prev, skills: (prev.skills ?? []).filter(s => s.id !== skillId) })
    setSelected(0)
  }

  const activeTab = TABS.find(t => t.key === tab) ?? TABS[0]
  let items = ev ? activeTab.itemsOf(ev) : []
  // On the Skills tab, surface the primary skill (the one this modal was opened
  // for) as the first pill so it lands selected. If the payload doesn't include
  // it, the order is left untouched and the first available pill is selected.
  if (ev && tab === 'skills' && primarySkill) {
    const norm = s => (s ?? '').trim().toLowerCase()
    const i = items.findIndex(it => norm(it.name) === norm(primarySkill))
    if (i > 0) items = [items[i], ...items.slice(0, i), ...items.slice(i + 1)]
  }
  const selectedItem = items[selected] ?? items[0]

  return (
    <div className={styles.backdrop} onClick={handleBackdropClick}>
      <div className={styles.modal} role="dialog" aria-modal="true" aria-label="Ticket Evidence">

        {/* ── Header ─────────────────────────────── */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <span className={styles.docIcon}>
              <Icon name="document" />
            </span>
            <div className={styles.headerText}>
              <Link
                className={styles.caseUid}
                to={`/cases/${encodeURIComponent(ev?.ticket_number ?? ticketId)}`}
                title="Open this case's ticket detail"
              >
                {ev?.ticket_number ?? ticketId}
              </Link>
              {ev?.short_description && (
                <span className={styles.caseTitle}>{ev.short_description}</span>
              )}
            </div>
          </div>
          <div className={styles.headerRight}>
            {ev?.resolving_agent && (
              <div className={styles.resolvingAgent}>
                <span className={styles.resolvingAgentLabel}>Resolving Agent</span>
                <span className={styles.resolvingAgentValue}>{ev.resolving_agent}</span>
              </div>
            )}
            <button className={styles.closeBtn} onClick={onClose} aria-label="Close">
              <Icon name="close" />
            </button>
          </div>
        </div>

        {/* ── Body ───────────────────────────────── */}
        <div className={styles.body}>
          {loading && (
            <div className={styles.loadingState}>
              <div className={styles.spinner} />
              <p>Loading evidence…</p>
            </div>
          )}

          {error && (
            <div className={styles.errorState}>
              <p>Failed to load evidence: {error}</p>
            </div>
          )}

          {ev && !loading && (
            <>
              {/* Summary */}
              <section className={styles.section}>
                <h3 className={styles.sectionLabel}>Summary</h3>
                <p className={styles.sectionContent}>{normalize(ev.ticket_summary)}</p>
              </section>

              {/* Resolution Notes — title + ROOT CAUSE chip, then the detail */}
              <section className={styles.section}>
                <div className={styles.sectionHeader}>
                  <h3 className={styles.sectionLabel}>Resolution Notes</h3>
                  {ev.root_cause_category && (
                    <span className={styles.rootCause}>
                      <span className={styles.rootCauseLabel}>Root Cause:</span>
                      <span className={styles.rootCauseValue}>{ev.root_cause_category}</span>
                    </span>
                  )}
                </div>
                <p className={styles.sectionContent}>{normalize(ev.root_cause_detail)}</p>
              </section>

              {removeError && (
                <div className={styles.removeError} role="alert">{removeError}</div>
              )}

              {/* Skills / Symptoms / Problems — pick an item (pills when they fit
                  one line, dropdown otherwise) to see its evidence below */}
              <section className={styles.section}>
                {/* Off-screen single-line copy used only to measure pill fit. */}
                <div className={styles.pillMeasure} ref={measureRef} aria-hidden="true">
                  {items.map((it, i) => (
                    <span key={i} className={styles.skillPill}>{it.name}</span>
                  ))}
                </div>

                <div className={styles.tabHeader}>
                  <div className={styles.tabs} role="tablist">
                    {TABS.map(t => (
                      <button
                        key={t.key}
                        role="tab"
                        aria-selected={t.key === tab}
                        className={`${styles.tab} ${t.key === tab ? styles.tabActive : ''}`}
                        onClick={() => selectTab(t.key)}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>

                  <div className={styles.tabHeaderRight}>
                    {/* Dropdown fallback — only when the pills would wrap to 2+ lines. */}
                    {items.length > 0 && overflowing && (
                      <select
                        className={styles.selector}
                        value={selected}
                        title={selectedItem?.name}
                        aria-label={`${activeTab.label} selector`}
                        onChange={e => setSelected(Number(e.target.value))}
                      >
                        {items.map((it, i) => (
                          <option key={i} value={i} title={it.name}>{it.name}</option>
                        ))}
                      </select>
                    )}

                    {/* Vote on the selected skill. Skills-only, and not shown for
                        candidate/proposed evidence. Keyed on skill id so an in-flight
                        vote can't land on whatever's selected after a switch. */}
                    {tab === 'skills' && selectedItem?.id != null && candidateSkillId == null && (
                      <VoteButtons
                        key={selectedItem.id}
                        subjectType="ticket_skill"
                        ticketId={ev.ticket_number}
                        skillId={selectedItem.id}
                        subjectName={selectedItem.name}
                        subjectDescription={selectedItem.detail}
                        confirmDown={{
                          title: 'Remove this skill?',
                          message: 'Down voting a linked skill will remove it from this case.',
                        }}
                        onVoted={direction => { if (direction === -1) handleSkillRemoved(selectedItem.id) }}
                      />
                    )}
                  </div>
                </div>

                <div className={styles.tabBody}>
                  {/* Pills — shown when the items fit on a single line. */}
                  {items.length > 0 && !overflowing && (
                    <div className={styles.skillPills}>
                      {items.map((it, i) => (
                        <button
                          key={i}
                          type="button"
                          aria-pressed={i === selected}
                          className={`${styles.skillPill} ${i === selected ? styles.skillPillActive : ''}`}
                          onClick={() => setSelected(i)}
                        >
                          {it.name}
                        </button>
                      ))}
                    </div>
                  )}

                  <div className={styles.tabDetail} role="tabpanel">
                    {items.length > 0 ? (
                      selectedItem?.detail
                        ? normalize(selectedItem.detail)
                        : <span className={styles.muted}>No details recorded.</span>
                    ) : (
                      <span className={styles.muted}>No {activeTab.label.toLowerCase()} recorded.</span>
                    )}
                  </div>
                </div>
              </section>
            </>
          )}
        </div>

      </div>
    </div>
  )
}

// Free-text fields carry CRLF line breaks; normalize to LF so the CSS
// `white-space: pre-line` renders them as line breaks without leaving stray
// carriage returns.
function normalize(text) {
  return (text ?? '').replace(/\r\n/g, '\n')
}
